import { Link } from 'react-router-dom';
import wordmark from '../assets/brand/wordmark-light.png';
import './Landing.css';

/**
 * Public Privacy Policy page (reachable at /privacy without login).
 * Text is a faithful rendering of mobile/PRIVACY_POLICY.md — styling only,
 * wording preserved. Reuses the landing's design tokens via the `.lp` wrapper.
 */
export default function Privacy() {
  return (
    <div className="lp">
      {/* Small brand header */}
      <header className="legal-head">
        <div className="legal-head-inner">
          <Link className="logo" to="/"><img src={wordmark} alt="Porrada" /></Link>
          <span className="legal-org">Zeng Digital</span>
        </div>
      </header>

      <main className="legal">
        <h1>Privacy Policy — Porrada (Mobile App)</h1>
        <p className="legal-eff"><strong>Effective date:</strong> 4 July 2026</p>

        <p>
          Porrada ("<strong>Porrada</strong>", "<strong>we</strong>", "<strong>us</strong>",
          or "<strong>our</strong>") is a mobile app for martial-arts and fitness gyms,
          operated by Gilbert Chan ("<strong>Zeng Digital</strong>"). This Privacy Policy
          explains what information we collect when you use the Porrada mobile app (the
          "<strong>App</strong>"), how we use it, and the choices you have.
        </p>
        <p>By using the App, you agree to this Privacy Policy.</p>

        <hr className="legal-rule" />

        <h2>1. Who this App is for</h2>
        <p>
          Porrada is provided to members, instructors, and staff of gyms that use Porrada
          to manage classes and attendance. When you use the App, you are associated with a
          specific gym (your "<strong>Gym</strong>"). Your Gym is responsible for managing
          its own members and has access to certain information about your activity at that
          Gym (see Section 4).
        </p>

        <hr className="legal-rule" />

        <h2>2. Information we collect</h2>

        <p className="legal-subhead"><strong>Information you provide to us:</strong></p>
        <ul>
          <li>
            <strong>Account and profile details:</strong> your name, email address, phone
            number, date of birth, and gender.
          </li>
          <li>
            <strong>Login credentials:</strong> your password (stored only in
            encrypted/hashed form — we never store it in plain text).
          </li>
          <li>
            <strong>Feedback:</strong> any messages you send us through the in-app feedback
            form.
          </li>
        </ul>

        <p className="legal-subhead">
          <strong>Information provided by your Gym or generated through use:</strong>
        </p>
        <ul>
          <li><strong>Training profile:</strong> your belt rank and stripes.</li>
          <li>
            <strong>Activity:</strong> the classes you book, cancel, and attend, and your
            check-in / attendance records.
          </li>
          <li>
            <strong>Membership status:</strong> your membership tier and status, as recorded
            by your Gym (shown to you as read-only).
          </li>
        </ul>

        <p className="legal-subhead"><strong>Information collected automatically:</strong></p>
        <ul>
          <li>
            <strong>Basic technical data</strong> needed to operate and secure the service,
            such as app and device information and log data (e.g. error and diagnostic
            information).
          </li>
        </ul>

        <p className="legal-subhead"><strong>What we do NOT collect:</strong></p>
        <ul>
          <li>
            <strong>No payment or card data.</strong> The App does not process payments or
            collect credit-card or financial information. Any billing is handled separately
            by your Gym outside the App.
          </li>
          <li>
            <strong>No location, camera, photos, or contacts.</strong> The App does not
            request or access your device location, camera, photo library, or contacts.
          </li>
          <li>
            We do not use third-party advertising or sell your personal information.
          </li>
        </ul>

        <hr className="legal-rule" />

        <h2>3. How we use your information</h2>
        <p>We use the information above to:</p>
        <ul>
          <li>create and manage your account and authenticate you;</li>
          <li>show you class schedules and let you book, cancel, and check in to classes;</li>
          <li>
            display your training profile (belt, stripes, attendance history) and membership
            status;
          </li>
          <li>let instructors and staff at your Gym manage rosters and attendance;</li>
          <li>respond to your feedback and provide support;</li>
          <li>keep the service secure, prevent abuse, and fix problems;</li>
          <li>comply with legal obligations.</li>
        </ul>

        <hr className="legal-rule" />

        <h2>4. How your information is shared</h2>
        <ul>
          <li>
            <strong>With your Gym.</strong> Instructors, staff, and owners at your Gym can
            see information relevant to running classes — for example your name, belt, class
            bookings, and attendance. This is a normal part of how the App works.
          </li>
          <li>
            <strong>With service providers.</strong> We host the service on cloud
            infrastructure (Amazon Web Services, located in the United States). These
            providers process data on our behalf under appropriate safeguards.
          </li>
          <li>
            <strong>For legal reasons.</strong> We may disclose information if required by
            law, or to protect the rights, safety, and security of our users or the service.
          </li>
          <li>
            <strong>We do not sell your personal information</strong>, and we do not share it
            for third-party advertising.
          </li>
        </ul>

        <hr className="legal-rule" />

        <h2>5. Data retention and deletion</h2>
        <p>
          We keep your information for as long as your account is active or as needed to
          provide the service.
        </p>
        <p>
          You can <strong>delete your account at any time</strong> from within the App
          (<strong>Profile → Edit profile → Delete account</strong>). When you delete your
          account, we remove or anonymize your personal information. Some records may be
          retained in de-identified form (for example, class attendance counts) or where
          retention is required for legal or legitimate business reasons.
        </p>

        <hr className="legal-rule" />

        <h2>6. Security</h2>
        <p>
          We protect your information using industry-standard measures, including encryption
          of data in transit (HTTPS) and secure on-device storage of your login token. No
          method of transmission or storage is 100% secure, but we work to protect your
          information and continually improve our safeguards.
        </p>

        <hr className="legal-rule" />

        <h2>7. Children's privacy</h2>
        <p>
          Porrada is intended for use by gym members. It is not directed to children under
          the age of 13 (or the minimum age in your country) without the involvement of a
          parent, guardian, or the Gym. If a minor uses the App, it should be under the
          supervision and with the consent of a parent/guardian or the managing Gym. If you
          believe a child has provided us personal information without appropriate consent,
          please contact us and we will delete it.
        </p>

        <hr className="legal-rule" />

        <h2>8. Your rights</h2>
        <p>
          Depending on where you live, you may have rights to access, correct, update, or
          delete your personal information, and to object to or restrict certain processing.
          You can update most of your details in the App, delete your account in the App, or
          contact us at the email below for other requests. Residents of the EU/EEA, UK, and
          California may have additional rights under applicable law.
        </p>

        <hr className="legal-rule" />

        <h2>9. International users</h2>
        <p>
          We operate and store data on infrastructure located in the United States. If you
          use the App from outside the United States, your information will be transferred to
          and processed in the United States, which may have different data-protection laws
          than your country.
        </p>

        <hr className="legal-rule" />

        <h2>10. Changes to this policy</h2>
        <p>
          We may update this Privacy Policy from time to time. If we make material changes,
          we will update the "Effective date" above and, where appropriate, notify you. Your
          continued use of the App after changes take effect means you accept the updated
          policy.
        </p>

        <hr className="legal-rule" />

        <h2>11. Contact us</h2>
        <p>If you have questions about this Privacy Policy or your information, contact us at:</p>
        <p className="legal-contact">
          <a href="mailto:zengdigital8@gmail.com">zengdigital8@gmail.com</a>
          <br />
          Gilbert Chan ("Zeng Digital")
        </p>

        <div className="legal-back">
          <Link to="/">← Back to home</Link>
        </div>
      </main>
    </div>
  );
}
